import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, Bot, Eye, Loader2, Plus, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import ProductSelectorModal from '../components/ProductSelectorModal';
import VariantForm, { ProductImage } from '../components/VariantForm';
import { buildThumbnailBlob } from "../utils/thumbnail";

interface ChatMessage {
  id: string;
  sender: 'user' | 'assistant';
  content: string;
  options?: string[];
  selectedProducts?: SelectedProduct[];
  generatedImages?: { id: number; url: string }[];
}

type SelectedProduct = { id: number; nombre: string; thumbnail: string | null };

export const TestV1: React.FC = () => {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProduct, setNewProduct] = useState<{ nombre: string; images: ProductImage[] }>({ nombre: '', images: [] });
  const [savingProduct, setSavingProduct] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [recentGenerated, setRecentGenerated] = useState<{ id: number; url: string }[]>([]);
  const [showGeneratedPanel, setShowGeneratedPanel] = useState(false);
  const [selectedGeneratedId, setSelectedGeneratedId] = useState<number | null>(null);
  const mobilePanelContentRef = useRef<HTMLDivElement>(null);
  const desktopPanelContentRef = useRef<HTMLDivElement>(null);
  const [activeStreamId, setActiveStreamId] = useState<string | null>(null);

  // Temporary debug flag for logging streaming behavior
  const DEBUG_STREAM = true;

  // Access guard: only allow dev testers (company_profiles.dev_tester = true)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (mounted) setAllowed(false); return; }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        const idCompany = (profile as any)?.id_company as string | null;
        if (!idCompany) { if (mounted) setAllowed(false); return; }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('dev_tester')
          .eq('id_company', idCompany)
          .single();
        if (mounted) setAllowed((company as any)?.dev_tester === true);
      } catch (_e) {
        if (mounted) setAllowed(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Redirect away if not allowed
  useEffect(() => {
    if (allowed === false) {
      navigate('/', { replace: true });
    }
  }, [allowed, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // Auto-resize the textarea height based on content
  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 192; // ~48 (max-h-48) px cap
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  };

  // Prefetch last 10 generated images (signed URLs) for instant access
  const prefetchRecentGenerated = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();
      const companyId = profile?.company_id as string | undefined;
      if (!companyId) return;

      const { data: rows } = await supabase
        .schema(companyId)
        .from('imagenes_generadas')
        .select('id, file_name, active, created_at')
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(10);

      const list: { id: number; url: string }[] = [];
      for (const r of rows || []) {
        const fileName = (r as any)?.file_name as string | undefined;
        if (!fileName) continue;
        const optPath = `generadas/opt/${fileName}`;
        const fullPath = `generadas/full/${fileName}`;
        let url: string | null = null;
        const { data: optSigned } = await supabase.storage.from(companyId).createSignedUrl(optPath, 3600);
        if (optSigned?.signedUrl) {
          url = optSigned.signedUrl;
        } else {
          const { data: fullSigned } = await supabase.storage.from(companyId).createSignedUrl(fullPath, 3600);
          url = fullSigned?.signedUrl || null;
        }
        if (url) {
          list.push({ id: (r as any).id as number, url });
          // Preload into browser cache for instant opening
          const img = new Image();
          img.src = url;
        }
      }
      setRecentGenerated(list);
      setSelectedGeneratedId(list.length > 0 ? list[0].id : null);
    } catch (e) {
      console.error('Error prefetching recent generated images:', e);
    }
  };

  useEffect(() => {
    // Warm up recent generated images on mount (skip while debugging to reduce noise)
    if (!DEBUG_STREAM) {
      prefetchRecentGenerated().catch(() => {});
    }
  }, [DEBUG_STREAM]);

  useEffect(() => {
    autoResize();
  }, [inputMessage]);

  // Helper: sign generated image URLs from Supabase storage using imagen IDs
  const signGeneratedImageThumbs = async (imageIds: number[], companyIdHint?: string) => {
    try {
      if (!Array.isArray(imageIds) || imageIds.length === 0) return [] as { id: number; url: string }[];
      let companyId = companyIdHint || '';
      if (!companyId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        const { data: profile } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .single();
        companyId = profile?.company_id || '';
      }
      if (!companyId) return [];

      const results: { id: number; url: string }[] = [];
      for (const imgId of imageIds) {
        try {
          const { data: imgRow } = await supabase
            .schema(companyId)
            .from('imagenes_generadas')
            .select('file_name, active')
            .eq('id', imgId)
            .single();
          const fileName = (imgRow as any)?.file_name as string | undefined;
          const isActive = (imgRow as any)?.active === true;
          if (!fileName || !isActive) continue;

          // Prefer optimized thumbnail if available; fallback to full
          const optPath = `generadas/opt/${fileName}`;
          const fullPath = `generadas/full/${fileName}`;
          let signedUrl: string | null = null;
          const { data: optSigned } = await supabase.storage.from(companyId).createSignedUrl(optPath, 3600);
          if (optSigned?.signedUrl) {
            signedUrl = optSigned.signedUrl;
          } else {
            const { data: fullSigned } = await supabase.storage.from(companyId).createSignedUrl(fullPath, 3600);
            signedUrl = fullSigned?.signedUrl || null;
          }
          if (signedUrl) results.push({ id: imgId, url: signedUrl });
        } catch (e) {
          // Skip invalid image rows silently
          continue;
        }
      }
      return results;
    } catch (e) {
      console.error('Error signing generated image thumbnails:', e);
      return [] as { id: number; url: string }[];
    }
  };

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setError(null);
    if (DEBUG_STREAM) console.log('[TestV1] sendMessage START. textLen:', trimmed.length);

    // Snapshot current selected products for this message
    const selectedSnapshot = selectedProducts.slice();

    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      sender: 'user',
      content: trimmed,
      selectedProducts: selectedSnapshot,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    // Clear selections in the composer after sending
    setSelectedProducts([]);
    setSending(true);
    setActiveStreamId(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const variantIds = selectedSnapshot.map((p) => p.id);
      if (DEBUG_STREAM) console.log('[TestV1] payload:', { user_id: user?.id, messageLen: trimmed.length, variantes_ids: variantIds });
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/7030210b-ab45-4ee1-a7a3-02e179388f36', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: user?.id, message: trimmed, variantes_ids: variantIds }),
      });
      if (DEBUG_STREAM) console.log('[TestV1] fetch completed. status:', response.status);

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      // Stream for SSE/NDJSON/JSON to support servers that stream JSON chunks
      const contentType = response.headers.get('content-type') || '';
      if (DEBUG_STREAM) console.log('[TestV1] fetch OK. content-type:', contentType, 'hasBody:', !!response.body);
      if (response.body && (contentType.includes('text/event-stream') || contentType.includes('application/x-ndjson') || contentType.includes('application/json'))) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let rawStream = '';
        // Aggregator for n8n typed stream: lines like {"type":"item","content":"..."}
        let jsonDocAgg = '';
        let builtText = '';
        let finalOptions: string[] | undefined;
        let finalImageIds: number[] | undefined;
        let finalCompanyId: string | undefined;
        const assistantId = `${Date.now()}-assistant`;
        let assistantCreated = false;
        if (DEBUG_STREAM) console.log('[TestV1] Streaming mode started');

        const flushAssistant = () => {
          if (DEBUG_STREAM) console.log('[TestV1] flushAssistant len:', builtText.length, 'options:', (finalOptions ?? []).length);
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: builtText, options: finalOptions } : m)));
        };

        const ensureAssistant = () => {
          if (!assistantCreated) {
            const msg: ChatMessage = { id: assistantId, sender: 'assistant', content: '' };
            setMessages((prev) => [...prev, msg]);
            setActiveStreamId(assistantId);
            assistantCreated = true;
            if (DEBUG_STREAM) console.log('[TestV1] ensureAssistant: created message with id', assistantId);
          }
        };

        // Extract partial response value from the aggregated JSON document string
        const updateResponseFromAgg = () => {
          const keyIdx = jsonDocAgg.indexOf('"response"');
          if (keyIdx === -1) return false;
          const afterKey = keyIdx + '"response"'.length;
          const colonIdx = jsonDocAgg.indexOf(':', afterKey);
          if (colonIdx === -1) return false;
          // Skip whitespace after colon
          let i = colonIdx + 1;
          while (i < jsonDocAgg.length && /\s/.test(jsonDocAgg[i])) i++;
          if (i >= jsonDocAgg.length || jsonDocAgg[i] !== '"') return false;
          const start = i + 1;
          // Find first non-escaped closing quote
          let end = -1;
          for (let j = start; j < jsonDocAgg.length; j++) {
            if (jsonDocAgg[j] === '"' && jsonDocAgg[j - 1] !== '\\') { end = j; break; }
          }
          const sliceEnd = end === -1 ? jsonDocAgg.length : end;
          let raw = jsonDocAgg.slice(start, sliceEnd);
          // Unescape common sequences for display
          raw = raw
            .replace(/\\n/g, '\n')
            .replace(/\\r/g, '\r')
            .replace(/\\t/g, '\t')
            .replace(/\\\"/g, '"')
            .replace(/\\\\/g, '\\');
          ensureAssistant();
          builtText = raw;
          if (DEBUG_STREAM) console.log('[TestV1] response partial len:', builtText.length, 'complete:', end !== -1);
          flushAssistant();
          return end !== -1;
        };

        const applyFinalImages = async () => {
          if (finalImageIds && finalImageIds.length > 0) {
            const thumbs = await signGeneratedImageThumbs(finalImageIds, finalCompanyId);
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, generatedImages: thumbs } : m)));
            if (thumbs.length > 0) {
              setRecentGenerated((prev) => {
                const map = new Map<number, string>();
                for (const g of thumbs) map.set(g.id, g.url);
                for (const p of prev) if (!map.has(p.id)) map.set(p.id, p.url);
                const arr = Array.from(map.entries()).map(([id, url]) => ({ id, url }));
                return arr.slice(0, 10);
              });
              setSelectedGeneratedId(thumbs[0].id);
            }
          }
        };

        const processEvent = (raw: string) => {
          let payloadStr = raw.trim();
          if (!payloadStr) return;
          // Handle SSE 'data:' prefix
          if (payloadStr.startsWith('data:')) {
            payloadStr = payloadStr.replace(/^data:\s*/i, '');
          }
          if (payloadStr === '[DONE]') return; // end marker for some servers
          if (DEBUG_STREAM) console.log('[TestV1] processEvent raw:', payloadStr.slice(0, 200));
          try {
            const obj = JSON.parse(payloadStr);
            if (DEBUG_STREAM) console.log('[TestV1] processEvent parsed keys:', Object.keys(obj));
            // n8n typed NDJSON stream: accumulate content fragments that form a JSON document
            if (typeof obj?.type === 'string' && typeof obj?.content === 'string') {
              // Ignore begin/end markers without content
              if (obj.type === 'item') {
                jsonDocAgg += obj.content;
                if (DEBUG_STREAM) console.log('[TestV1] typed-stream agg len:', jsonDocAgg.length);
                // Update assistant with partial response while the JSON doc is still incomplete
                updateResponseFromAgg();
                try {
                  const doc = JSON.parse(jsonDocAgg);
                  if (DEBUG_STREAM) console.log('[TestV1] typed-stream parsed keys:', Object.keys(doc));
                  ensureAssistant();
                  if (typeof doc.response === 'string') {
                    builtText = doc.response;
                    flushAssistant();
                  }
                  if (Array.isArray(doc.options) || Array.isArray(doc.opciones)) {
                    finalOptions = (doc.options ?? doc.opciones) as string[];
                  }
                  if (Array.isArray(doc.imagenes_generadas_ids)) {
                    finalImageIds = doc.imagenes_generadas_ids as number[];
                  }
                  if (typeof doc.company_id === 'string') {
                    finalCompanyId = doc.company_id as string;
                  }
                } catch {
                  // Not yet a complete JSON document; keep accumulating
                }
              }
              return; // Do not append raw content pieces to the message
            }
            // Prefer explicit JSON streaming contract
            if (typeof obj.response === 'string') {
              ensureAssistant();
              builtText = obj.response; // treat 'response' as full text so far
              if (DEBUG_STREAM) console.log('[TestV1] using response, len:', obj.response.length);
              flushAssistant();
            } else {
              // Fallback to delta-style append
              const delta = obj.delta ?? obj.text ?? obj.content ?? '';
              if (typeof delta === 'string' && delta.length > 0) {
                ensureAssistant();
                builtText += delta;
                if (DEBUG_STREAM) console.log('[TestV1] using delta piece:', delta.slice(0, 120));
                flushAssistant();
              }
            }
            if (Array.isArray(obj.options) || Array.isArray(obj.opciones)) {
              finalOptions = (obj.options ?? obj.opciones) as string[];
              if (DEBUG_STREAM) console.log('[TestV1] options count now:', finalOptions.length);
            }
            if (Array.isArray(obj.imagenes_generadas_ids)) {
              finalImageIds = obj.imagenes_generadas_ids as number[];
              if (DEBUG_STREAM) console.log('[TestV1] imagenes_generadas_ids:', finalImageIds);
            }
            if (typeof obj.company_id === 'string') {
              finalCompanyId = obj.company_id as string;
              if (DEBUG_STREAM) console.log('[TestV1] company_id:', finalCompanyId);
            }
          } catch (_e) {
            // Ignore non-JSON tokens in streaming mode (server promises JSON streaming)
            if (DEBUG_STREAM) console.log('[TestV1] JSON parse error for chunk (will ignore):', payloadStr.slice(0, 120));
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          if (DEBUG_STREAM) console.log('[TestV1] chunk size:', chunk.length, 'preview:', chunk.slice(0, 120));
          rawStream += chunk;
          buffer += chunk;

          // Try to split by SSE event boundary first
          let parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) {
            // Join multiple 'data:' lines into a single payload for this event
            const lines = part.split('\n');
            const payload = lines
              .filter((l) => l.startsWith('data:'))
              .map((l) => l.replace(/^data:\s*/i, ''))
              .join('\n');
            if (payload) {
              if (DEBUG_STREAM) console.log('[TestV1] SSE event payload:', payload.slice(0, 200));
              processEvent(payload);
            }
          }

          // If no 'data:' present, try NDJSON by lines
          if (!buffer.includes('data:')) {
            const byLines = buffer.split('\n');
            buffer = byLines.pop() || '';
            for (const line of byLines) {
              if (DEBUG_STREAM) console.log('[TestV1] NDJSON line:', line.slice(0, 200));
              processEvent(line);
            }
          }
        }

        // Process any trailing buffer
        if (buffer.trim()) {
          if (DEBUG_STREAM) console.log('[TestV1] trailing buffer:', buffer.slice(0, 200));
          processEvent(buffer);
        }

        // If nothing was built but we have a raw JSON stream, parse once and render
        if (builtText.trim().length === 0 && jsonDocAgg.trim().length > 0) {
          try {
            const doc = JSON.parse(jsonDocAgg.trim());
            const assistantText = typeof doc.response === 'string' ? doc.response : 'Lo siento, no pude procesar tu mensaje.';
            const options: string[] | undefined = Array.isArray(doc.options) ? doc.options : (Array.isArray(doc.opciones) ? doc.opciones : undefined);
            if (assistantCreated) {
              setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: assistantText, options } : m)));
            } else {
              setMessages((prev) => [...prev, { id: assistantId, sender: 'assistant', content: assistantText, options }]);
              assistantCreated = true;
            }
            if (Array.isArray(doc.imagenes_generadas_ids) && doc.imagenes_generadas_ids.length > 0) {
              const thumbs = await signGeneratedImageThumbs(doc.imagenes_generadas_ids as number[], typeof doc.company_id === 'string' ? doc.company_id : undefined);
              if (thumbs.length > 0) {
                setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, generatedImages: thumbs } : m)));
                setRecentGenerated((prev) => {
                  const map = new Map<number, string>();
                  for (const g of thumbs) map.set(g.id, g.url);
                  for (const p of prev) if (!map.has(p.id)) map.set(p.id, p.url);
                  const arr = Array.from(map.entries()).map(([id, url]) => ({ id, url }));
                  return arr.slice(0, 10);
                });
                setSelectedGeneratedId(thumbs[0].id);
              }
            }
          } catch {}
        } else if (builtText.trim().length === 0 && rawStream.trim().length > 0) {
          try {
            const fallback = JSON.parse(rawStream.trim());
            const assistantText = typeof fallback.response === 'string' ? fallback.response : 'Lo siento, no pude procesar tu mensaje.';
            const options: string[] | undefined = Array.isArray(fallback.options) ? fallback.options : (Array.isArray(fallback.opciones) ? fallback.opciones : undefined);
            if (assistantCreated) {
              setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: assistantText, options } : m)));
            } else {
              setMessages((prev) => [...prev, { id: assistantId, sender: 'assistant', content: assistantText, options }]);
              assistantCreated = true;
            }
            if (DEBUG_STREAM) console.log('[TestV1] fallback rawStream parsed. response len:', typeof (fallback.response) === 'string' ? fallback.response.length : 'n/a');
            if (Array.isArray(fallback.imagenes_generadas_ids) && fallback.imagenes_generadas_ids.length > 0) {
              const thumbs = await signGeneratedImageThumbs(fallback.imagenes_generadas_ids as number[], typeof fallback.company_id === 'string' ? fallback.company_id : undefined);
              if (thumbs.length > 0) {
                setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, generatedImages: thumbs } : m)));
                setRecentGenerated((prev) => {
                  const map = new Map<number, string>();
                  for (const g of thumbs) map.set(g.id, g.url);
                  for (const p of prev) if (!map.has(p.id)) map.set(p.id, p.url);
                  const arr = Array.from(map.entries()).map(([id, url]) => ({ id, url }));
                  return arr.slice(0, 10);
                });
                setSelectedGeneratedId(thumbs[0].id);
                if (DEBUG_STREAM) console.log('[TestV1] fallback applied images:', thumbs.map(t => t.id));
              }
            }
          } catch {}
        } else {
          // Apply final images and options collected during streaming
          if (DEBUG_STREAM) console.log('[TestV1] streaming finished. builtTextLen:', builtText.length, 'assistantCreated:', assistantCreated, 'finalOptions:', (finalOptions ?? []).length, 'finalImageIds:', finalImageIds?.length || 0);
          await applyFinalImages();
        }
      } else {
        // Fallback: non-streaming JSON (or plain text)
        let data: any = null;
        try {
          data = await response.json();
          if (DEBUG_STREAM) console.log('[TestV1] non-streaming JSON parsed. response len:', typeof data?.response === 'string' ? data.response.length : 'n/a', 'options:', Array.isArray(data?.options) ? data.options.length : (Array.isArray(data?.opciones) ? data.opciones.length : 0));
        } catch (_e) {
          const text = await response.text();
          if (DEBUG_STREAM) console.log('[TestV1] non-streaming text fallback. len:', text?.length || 0, 'preview:', (text || '').slice(0, 200));
          // If it looks like JSON, try to parse and extract fields
          if (text && text.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(text);
              data = parsed;
              if (DEBUG_STREAM) console.log('[TestV1] non-streaming text looked like JSON. parsed successfully.');
            } catch {
              // Show raw text if not valid JSON
              const assistantMessage: ChatMessage = {
                id: `${Date.now()}-assistant`,
                sender: 'assistant',
                content: text || 'Lo siento, no pude procesar tu mensaje.',
              };
              setMessages((prev) => [...prev, assistantMessage]);
              return;
            }
          } else {
            const assistantMessage: ChatMessage = {
              id: `${Date.now()}-assistant`,
              sender: 'assistant',
              content: text || 'Lo siento, no pude procesar tu mensaje.',
            };
            setMessages((prev) => [...prev, assistantMessage]);
            return;
          }
        }
        const assistantText = data?.response ?? 'Lo siento, no pude procesar tu mensaje.';
        const options: string[] | undefined = Array.isArray(data?.options) ? data.options : (Array.isArray(data?.opciones) ? data.opciones : undefined);
        const generatedIds: number[] | undefined = Array.isArray(data?.imagenes_generadas_ids)
          ? (data.imagenes_generadas_ids as number[])
          : undefined;
        const companyIdFromResponse: string | undefined = typeof data?.company_id === 'string' ? data.company_id : undefined;

        // 1) Show assistant text immediately
        const assistantId = `${Date.now()}-assistant`;
        setMessages((prev) => [
          ...prev,
          { id: assistantId, sender: 'assistant', content: assistantText, options },
        ]);

        // 2) Attach images asynchronously (if any)
        if (generatedIds && generatedIds.length > 0) {
          const thumbs = await signGeneratedImageThumbs(generatedIds, companyIdFromResponse);
          if (thumbs.length > 0) {
            setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, generatedImages: thumbs } : m)));
            setRecentGenerated((prev) => {
              const map = new Map<number, string>();
              for (const g of thumbs) map.set(g.id, g.url);
              for (const p of prev) if (!map.has(p.id)) map.set(p.id, p.url);
              const arr = Array.from(map.entries()).map(([id, url]) => ({ id, url }));
              return arr.slice(0, 10);
            });
            setSelectedGeneratedId(thumbs[0].id);
          }
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err.message : 'Error al enviar el mensaje');
    } finally {
      setSending(false);
      setActiveStreamId(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void sendMessage(inputMessage);
  };

  const handleOptionClick = (option: string) => {
    void sendMessage(option);
  };

  const removeSelectedProduct = (id: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== id));
  };

  // Save new product (same behavior as SeleccionarProductosAnuncio add flow)
  const handleSaveProduct = async () => {
    const productName = newProduct.nombre;
    if (!productName) return;
    try {
      setSavingProduct(true);
      setImageError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      // 1) Create product
      const { data: producto, error: productoError } = await supabase
        .schema(profile.company_id)
        .from('productos')
        .insert({ nombre: productName, tiene_variantes: false })
        .select('id')
        .single();
      if (productoError) throw productoError;

      const productId = producto.id as number;

      // 2) Create variant for that product
      const { data: variant, error: variantError } = await supabase
        .schema(profile.company_id)
        .from('variantes')
        .insert({ nombre: productName, activo: true, product_id: productId })
        .select('id')
        .single();
      if (variantError) throw variantError;

      const variantId = variant.id as number;

      // 3) Upload images and insert DB rows storing only filename
      for (let i = 0; i < newProduct.images.length; i++) {
        const imgData = newProduct.images[i];
        if (!imgData.tempFile) continue;

        // Validate file size (limit: 100 MB)
        const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
        if ((imgData.tempFile as File).size > MAX_FILE_SIZE) {
          throw new Error('El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.');
        }

        // Unique filename - sanitize to remove spaces and special characters
        const timestamp = Date.now();
        const originalName = imgData.fileName || (imgData.tempFile as File).name;
        const sanitizedName = originalName.replace(/[^a-zA-Z0-9._-]/g, '_');
        const fileBaseName = `${timestamp}_${sanitizedName}`;

        // Upload full
        const idCompany = (profile as any).id_company ?? profile.company_id;
        const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
        const { error: fullErr } = await supabase.storage
          .from('productos')
          .upload(fullPath, imgData.tempFile as File);
        if (fullErr) throw fullErr;

        // Create optimized thumbnail using canvas
        try {
          const blob = await buildThumbnailBlob(imgData.tempFile as File);
          if (!blob) throw new Error('no se pudo generar el thumbnail');

          const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
          const { error: optErr } = await supabase.storage
            .from('productos')
            .upload(optPath, blob);
          if (optErr) console.warn('Optimized upload failed, continuing with full only', optErr);
        } catch (optErr) {
          console.warn('Error creating optimized image, continuing', optErr);
        }

        // Insert DB row with filename only
        const { error: imageRowErr } = await supabase
          .schema(profile.company_id)
          .from('productos_imagenes')
          .insert({ variante_id: variantId, url: fileBaseName, principal: imgData.principal || i === 0, orden: imgData.orden || i + 1 });
        if (imageRowErr) throw imageRowErr;
      }

      // Reset and refresh
      setShowAddForm(false);
      setNewProduct({ nombre: '', images: [] });
      setRefreshKey((k) => k + 1); // Refresh product modal content if opened
    } catch (err) {
      console.error('Error saving product (TestV1):', err);
      setImageError(err instanceof Error ? err.message : 'Error al guardar producto');
    } finally {
      setSavingProduct(false);
    }
  };

  const selectedGenerated = recentGenerated.find((i) => i.id === selectedGeneratedId) || null;

  const handleSelectRecentGenerated = (id: number, panel: 'mobile' | 'desktop') => {
    setSelectedGeneratedId(id);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {}
    if (panel === 'mobile') {
      mobilePanelContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      desktopPanelContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Ensure a single image is present in the recent list; if missing, fetch and add it
  const ensureImageInRecent = async (imageId: number) => {
    const exists = recentGenerated.some((r) => r.id === imageId);
    if (exists) return;
    const [result] = await signGeneratedImageThumbs([imageId]);
    if (result) {
      setRecentGenerated((prev) => {
        const map = new Map<number, string>([[result.id, result.url]]);
        for (const p of prev) if (!map.has(p.id)) map.set(p.id, p.url);
        const arr = Array.from(map.entries()).map(([id, url]) => ({ id, url }));
        return arr.slice(0, 10);
      });
    }
  };

  // Open the generated images panel focusing the provided image id
  const openGeneratedPanelFor = async (imageId: number) => {
    await ensureImageInRecent(imageId);
    setSelectedGeneratedId(imageId);
    setShowGeneratedPanel(true);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {}
    // Defer panel scroll to ensure refs are mounted
    setTimeout(() => {
      mobilePanelContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      desktopPanelContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  // Loading state while checking permissions
  if (allowed === null) {
    return (
      <div className="min-h-screen bg-[#EFF0F5] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  // If unauthorized, the redirect effect will send the user home
  if (allowed === false) {
    return null;
  }

  return (
    <div className="flex flex-col h-screen max-h-screen bg-white">
      {/* Header (simple) */}
      <div className="flex items-center justify-between p-4 border-b">
        <h1 className="text-xl font-semibold">Test V1</h1>
      </div>

      {/* Messages area */}
      <div className={`flex-1 overflow-y-auto p-4 ${showGeneratedPanel ? 'md:mr-[33vw]' : ''}`}>
        <div className="max-w-3xl mx-auto space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700">
              <span className="text-sm">{error}</span>
            </div>
          )}

          {messages.map((msg) => {
            // Derive display values so we never render raw JSON in the bubble
            let displayContent: string = msg.content;
            let displayOptions: string[] | undefined = msg.options;
            if (msg.sender === 'assistant' && typeof msg.content === 'string') {
              const raw = msg.content.trim();
              if (raw.startsWith('{') && raw.includes('"response"')) {
                try {
                  const parsed: any = JSON.parse(raw);
                  if (typeof parsed?.response === 'string') displayContent = parsed.response;
                  const opts = Array.isArray(parsed?.options) ? parsed.options : (Array.isArray(parsed?.opciones) ? parsed.opciones : undefined);
                  if (!displayOptions && opts) displayOptions = opts as string[];
                } catch {}
              }
            }
            return (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] md:max-w-[75%] ${msg.sender === 'user' ? 'ml-6 md:ml-0' : 'mr-6 md:mr-0'}`}>
                  {/* Selected products snapshot for user messages */}
                  {msg.sender === 'user' && Array.isArray(msg.selectedProducts) && msg.selectedProducts.length > 0 && (
                    <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {msg.selectedProducts.map((p) => (
                        <div key={`${msg.id}-sp-${p.id}`} className="flex flex-col items-center">
                          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                            {p.thumbnail ? (
                              <img src={p.thumbnail} alt={p.nombre} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gray-100" />
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-700 text-center w-24 sm:w-28 truncate">{p.nombre}</div>
                        </div>
                      ))}
                    </div>
                  )}

                {/* Message bubble */}
                <div
                  className={`p-3 md:p-4 rounded-2xl ${
                    msg.sender === 'user'
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.sender === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2 text-gray-700">
                      <Bot className="w-4 h-4 text-blue-500" />
                      <span className="text-sm">Asistente</span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm md:text-base">{displayContent}</p>
                </div>

                {/* Assistant generated images thumbnails (outside bubble) */}
                {msg.sender === 'assistant' && Array.isArray(msg.generatedImages) && msg.generatedImages.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {msg.generatedImages.map((g) => (
                      <div key={`${msg.id}-gi-${g.id}`} className="flex flex-col items-center">
                        <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                          <img src={g.url} alt={`Imagen generada ${g.id}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => openGeneratedPanelFor(g.id)}
                            className="absolute bottom-1.5 right-1.5 px-2 py-0.5 rounded-full text-xs bg-blue-500 hover:bg-blue-600 text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Ver
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Options under assistant message */}
                {msg.sender === 'assistant' && Array.isArray(displayOptions) && displayOptions.length > 0 && (
                  <div className="mt-3 flex flex-col gap-2">
                    {displayOptions.map((opt, idx) => (
                      <button
                        key={`${msg.id}-opt-${idx}`}
                        type="button"
                        onClick={() => handleOptionClick(opt)}
                        className="px-3 py-1 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            );
          })}

          {sending && !activeStreamId && (
            <div className="flex justify-start">
              <div className="max-w-[90%] md:max-w-[75%] p-3 md:p-4 rounded-2xl bg-gray-100 text-gray-800 mr-6 md:mr-0">
                <div className="flex items-center gap-2">
                  <Bot className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Asistente</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  <span className="text-gray-500 text-sm">Escribiendo...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Bottom input container */}
      <div className={`p-4 ${showGeneratedPanel ? 'md:mr-[33vw]' : ''}`}>
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-3">
            {/* Text area at top */}
            <div>
              {selectedProducts.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {selectedProducts.map((p) => (
                    <div key={p.id} className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-gray-200 bg-white shadow-sm">
                      {p.thumbnail ? (
                        <img src={p.thumbnail} alt={p.nombre} className="w-6 h-6 rounded-md object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-md bg-gray-200" />
                      )}
                      <span className="text-sm text-gray-700 max-w-[140px] truncate">{p.nombre}</span>
                      <button
                        type="button"
                        onClick={() => removeSelectedProduct(p.id)}
                        className="p-1 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        aria-label={`Quitar ${p.nombre}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <textarea
                ref={textareaRef}
                rows={1}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="w-full px-3 py-2 rounded-md border-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-y-auto min-h-[36px] max-h-48"
                disabled={sending}
              />
            </div>

            {/* Buttons row below */}
            <div className="mt-3 flex items-center justify-between">
              {/* Left: Productos pill and two icon-only buttons */}
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm">Productos</span>
                <button
                  type="button"
                  title="Ver productos"
                  className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={() => setShowProductModal(true)}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  title="Agregar producto"
                  className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus className="w-4 h-4" />
                </button>
                {/* Generadas pill */}
                <button
                  type="button"
                  title="Ver imágenes generadas recientes"
                  onClick={() => {
                    if (!showGeneratedPanel) prefetchRecentGenerated().catch(() => {});
                    setShowGeneratedPanel(!showGeneratedPanel);
                  }}
                  className={`h-9 inline-flex items-center gap-2 px-3 rounded-full border text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${showGeneratedPanel ? 'bg-blue-500 hover:bg-blue-600 text-white border-blue-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                >
                  <ImageIcon className="w-4 h-4" />
                  Generadas
                </button>
              </div>

              {/* Right: Send button (blue circular with up arrow) */}
              <div>
                <button
                  type="submit"
                  disabled={sending || !inputMessage.trim()}
                  className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  aria-label="Enviar"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowUp className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Mobile slide-in panel for recent generated images */}
      <div className={`md:hidden fixed inset-0 z-40 bg-white transform transition-transform duration-300 ease-in-out ${showGeneratedPanel ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Generadas</h2>
          <button
            type="button"
            onClick={() => setShowGeneratedPanel(false)}
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div ref={mobilePanelContentRef} className="flex-1 p-4 overflow-y-auto">
          {selectedGenerated && (
            <div className="mb-4">
              <div className="w-full rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm">
                <img src={selectedGenerated.url} alt={`Generada ${selectedGenerated.id}`} className="w-full h-auto max-h-[70vh] object-contain bg-gray-50" />
              </div>
            </div>
          )}
          {recentGenerated.length === 0 ? (
            <p className="text-sm text-gray-500">No hay imágenes generadas todavía.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {recentGenerated.map((img) => {
                const selected = selectedGeneratedId === img.id;
                return (
                  <button
                    key={`rec-m-${img.id}`}
                    type="button"
                    onClick={() => handleSelectRecentGenerated(img.id, 'mobile')}
                    className={`relative w-full aspect-square rounded-xl overflow-hidden border ${selected ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    <img src={img.url} alt={`Generada ${img.id}`} className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Desktop right panel (1/3 width) */}
      {showGeneratedPanel && (
        <div className="hidden md:flex fixed right-0 top-0 bottom-0 w-1/3 bg-white border-l border-gray-200 shadow-sm z-30">
          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Generadas</h2>
              <button
                type="button"
                onClick={() => setShowGeneratedPanel(false)}
                className="p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                aria-label="Cerrar panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div ref={desktopPanelContentRef} className="flex-1 p-4 overflow-y-auto">
              {selectedGenerated && (
                <div className="mb-4">
                  <div className="w-full rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm">
                    <img src={selectedGenerated.url} alt={`Generada ${selectedGenerated.id}`} className="w-full h-auto max-h-[65vh] object-contain bg-gray-50" />
                  </div>
                </div>
              )}
              {recentGenerated.length === 0 ? (
                <p className="text-sm text-gray-500">No hay imágenes generadas todavía.</p>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {recentGenerated.map((img) => {
                    const selected = selectedGeneratedId === img.id;
                    return (
                      <button
                        key={`rec-d-${img.id}`}
                        type="button"
                        onClick={() => handleSelectRecentGenerated(img.id, 'desktop')}
                        className={`relative w-full aspect-square rounded-xl overflow-hidden border ${selected ? 'ring-2 ring-blue-500 ring-offset-2 border-blue-500' : 'border-gray-200'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                      >
                        <img src={img.url} alt={`Generada ${img.id}`} className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modals */}
      {showProductModal && (
        <ProductSelectorModal
          isOpen={showProductModal}
          onClose={() => setShowProductModal(false)}
          refreshKey={refreshKey}
          onAddProduct={() => setShowAddForm(true)}
          onConfirm={(items) => {
            setSelectedProducts(items as SelectedProduct[]);
          }}
        />
      )}

      {showAddForm && (
        <VariantForm
          editingVariant={null}
          newProduct={newProduct}
          loading={savingProduct}
          uploadingImages={{}}
          imageError={imageError}
          onCancel={() => setShowAddForm(false)}
          onSave={handleSaveProduct}
          onImageUpload={() => {}}
          onDeleteImage={() => {}}
          onSetPrincipalImage={() => {}}
          onReorderImages={() => {}}
          setEditingVariant={() => {}}
          setNewProduct={setNewProduct}
        />
      )}
    </div>
  );
};

export default TestV1;

